import type { CardMachine, CardSplit, CardPaymentKind } from '@/types/database';

export type { CardMachine, CardSplit, CardPaymentKind };

export const PAYMENT_KIND_LABELS: Record<CardPaymentKind, string> = {
  debit: 'Débito',
  credit_1x: 'Crédito à vista',
  credit_installments: 'Crédito parcelado',
};

export interface SplitCalcInput {
  grossAmount: number;
  installments: number;
  paymentKind: CardPaymentKind;
  mdrPct: number; // % da maquininha para o tipo escolhido
  anticipationRateMonthlyPct: number; // 0 se não antecipa
  anticipate: boolean;
}

export interface SplitCalcResult {
  grossAmount: number;
  mdrPct: number;
  mdrAmount: number;
  anticipationRateMonthlyPct: number;
  anticipationAmount: number;
  netAmount: number;
  effectiveCostPct: number; // (gross - net) / gross * 100
}

/**
 * Cálculo de antecipação. Premissa pragmática:
 * - MDR aplica sobre o bruto.
 * - Antecipação aplica sobre saldo (gross - mdr) usando média ponderada de meses.
 *   N_avg ≈ (installments + 1) / 2 para débito/crédito 1x dá 1; parcelado dá centro.
 */
export function calculateSplit({
  grossAmount,
  installments,
  paymentKind,
  mdrPct,
  anticipationRateMonthlyPct,
  anticipate,
}: SplitCalcInput): SplitCalcResult {
  const gross = Math.max(0, grossAmount);
  const mdrAmount = round2((gross * mdrPct) / 100);
  const afterMdr = round2(gross - mdrAmount);

  let anticipationAmount = 0;
  if (anticipate && anticipationRateMonthlyPct > 0 && installments > 1) {
    // Média de meses até o pagamento original (antes da antecipação)
    // Parcelado: parcela i cai em i meses; média = (1 + N) / 2
    const avgMonths = (installments + 1) / 2 - 1; // subtrai 1 (parcela 1 já cai mês 1)
    const months = Math.max(0, avgMonths);
    anticipationAmount = round2(afterMdr * (anticipationRateMonthlyPct / 100) * months);
  } else if (anticipate && paymentKind === 'credit_1x' && anticipationRateMonthlyPct > 0) {
    // Crédito 1x: assume D+30 → antecipação de 1 mês
    anticipationAmount = round2((afterMdr * anticipationRateMonthlyPct) / 100);
  }

  const netAmount = round2(afterMdr - anticipationAmount);
  const totalCost = round2(gross - netAmount);
  const effectiveCostPct = gross > 0 ? round4((totalCost / gross) * 100) : 0;

  return {
    grossAmount: round2(gross),
    mdrPct,
    mdrAmount,
    anticipationRateMonthlyPct: anticipate ? anticipationRateMonthlyPct : 0,
    anticipationAmount,
    netAmount,
    effectiveCostPct,
  };
}

export function pickMdrPct(machine: CardMachine, kind: CardPaymentKind): number {
  if (kind === 'debit') return Number(machine.mdr_debit_pct);
  if (kind === 'credit_1x') return Number(machine.mdr_credit_pct);
  return Number(machine.mdr_installments_pct);
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}
function round4(value: number): number {
  return Math.round(value * 10000) / 10000;
}
